import { useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";

import Button from "components/atoms/Button/Button";
import { ReactComponent as ExclamationIcon } from "assets/images/icons/Exclamation.svg";
import { IProviderProps } from "./quotes/QuotesType";

interface IParcelQuoteErrorsProps {
  providers: IProviderProps[];
}

interface IErrorGroup {
  label: string;
  messages: string[];
}

const COLLAPSED_COUNT = 1;

function parseErrorGroup(errorMessage: string): IErrorGroup | null {
  if (!errorMessage) return null;
  const lines = errorMessage.split("\n").map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return null;
  return { label: lines[0], messages: lines.slice(1) };
}

function ParcelQuoteErrors({ providers }: IParcelQuoteErrorsProps) {
  const intl = useIntl();
  const [errorGroups, setErrorGroups] = useState<IErrorGroup[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const groups: IErrorGroup[] = [];

    providers?.forEach(({ auto_quote_error_message, auto_quote_help_text, company_name }) => {
      if (auto_quote_error_message) {
        const group = parseErrorGroup(auto_quote_error_message);
        if (group) groups.push(group);
      }
      if (auto_quote_help_text) {
        groups.push({
          label: company_name,
          messages: [`There was an error while posting booking data to ${company_name}, ${auto_quote_help_text}`],
        });
      }
    });

    setErrorGroups(groups);
  }, [providers]);

  const onExpand = () => {
    setIsExpanded(!isExpanded);
  };

  if (!errorGroups.length) return null;

  const visibleGroups = isExpanded ? errorGroups : errorGroups.slice(0, COLLAPSED_COUNT);

  return (
    <Row>
      <Col className="mb-xl" style={{ transform: "translateY(24px)" }}>
        <div
          className="pb-4 pl-4 pr-3 pt-3 bg-warning"
          style={{ borderRadius: "16px 16px 0px 0px", paddingBottom: "10px" }}
        >
          <div className="d-flex flex-column text-warning" style={{ verticalAlign: "text-top" }}>
            <div className="d-flex">
              <div>
                <ExclamationIcon className="mr-3 custom-alert-warning-icon" style={{ minWidth: 16 }} />
              </div>

              <div>
                {visibleGroups.map((group) => (
                  <div key={group.label} className="mb-1">
                    <div className="font-weight-bold">{group.label}</div>
                    {group.messages.length > 0 && (
                      <ul className="mb-0 pl-3">
                        {group.messages.map((msg) => (
                          <li key={msg}>{msg}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
                {errorGroups.length > COLLAPSED_COUNT && (
                  <div>
                    <Button variant="link" onClick={onExpand} className="font-weight-bold p-0 pt-2 pb-1 text-warning">
                      {isExpanded
                        ? intl.formatMessage({ id: "general.viewLess", defaultMessage: "View less" })
                        : intl.formatMessage({ id: "general.viewMore", defaultMessage: "View more" })}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Col>
    </Row>
  );
}

ParcelQuoteErrors.propTypes = {
  providers: PropTypes.arrayOf(PropTypes.shape({})),
};

ParcelQuoteErrors.defaultProps = {
  providers: null,
};

export default ParcelQuoteErrors;
